import type { ManualChapter } from './types'

export const documentsChapter: ManualChapter = {
  id: 'documents',
  number: 6,
  title: 'Documenti',
  description: 'Repository documentale con versioning',
  icon: 'DocumentTextIcon',
  sections: [
    {
      id: 'repository',
      title: 'Repository Documentale',
      requirement: '§2.5 Upload documenti con versionamento',
      content: `
## Repository Documentale

Il repository è la libreria centrale di tutti i documenti della community.

![Repository Documenti](documents.png)
*La libreria documenti*

### Struttura

I documenti sono organizzati in:
- **Categorie** - Aree tematiche principali
- **Cartelle** - Sottocartelle per organizzazione
- **Tag** - Etichette per classificazione trasversale

### Navigazione

1. Clicca su **"Documenti"** nel menu
2. Scegli una **categoria** o naviga le cartelle
3. Clicca su un documento per visualizzarlo

### Viste Disponibili

| Vista | Descrizione |
|-------|-------------|
| **Griglia** | Anteprime come schede |
| **Lista** | Elenco dettagliato |
`,
      screenshots: [
        {
          id: 'documents',
          path: '/assets/manual/documents.png',
          caption: 'Repository documenti',
          alt: 'Libreria documenti con categorie e file'
        }
      ]
    },
    {
      id: 'search',
      title: 'Ricerca Documenti',
      requirement: '§2.5 Ricerca full-text interna ai documenti',
      content: `
## Ricerca Documenti

### Ricerca Full-Text

La ricerca cerca all'interno:
- **Titolo** del documento
- **Descrizione**
- **Contenuto** del file (PDF, Word, etc.)
- **Tag** associati

### Come Cercare

1. Usa la **barra di ricerca** in alto
2. Digita le parole chiave
3. Premi **Invio**

### Filtri

Affina i risultati con:

| Filtro | Opzioni |
|--------|---------|
| **Categoria** | Seleziona area tematica |
| **Tipo file** | PDF, Word, Excel, etc. |
| **Data** | Ultimi 7 giorni, mese, anno |
| **Autore** | Chi ha caricato il documento |

### Suggerimenti

- Usa **"virgolette"** per frasi esatte
- Combina più parole per risultati specifici
- Filtra per tipo se cerchi un formato particolare
`
    },
    {
      id: 'view-download',
      title: 'Visualizzazione e Download',
      content: `
## Visualizzazione e Download

### Anteprima

Per molti formati è disponibile l'anteprima nel browser:
- PDF
- Immagini (JPG, PNG)
- Documenti Office (con viewer)

### Download

1. Trova il documento desiderato
2. Clicca sull'icona **⬇️ Download**
3. Il file viene scaricato sul tuo dispositivo

### Informazioni Documento

Ogni documento mostra:

| Info | Descrizione |
|------|-------------|
| **Titolo** | Nome del documento |
| **Descrizione** | Breve spiegazione |
| **Categoria** | Area tematica |
| **Tipo file** | PDF, DOCX, XLSX, etc. |
| **Dimensione** | Peso del file |
| **Autore** | Chi l'ha caricato |
| **Data** | Quando è stato aggiunto |
| **Download** | Numero di download |
| **Versione** | Versione corrente |
`
    },
    {
      id: 'versioning',
      title: 'Versioning',
      requirement: '§2.5 Classificazione per categorie e metadati',
      content: `
## Versioning dei Documenti

Il sistema tiene traccia delle diverse versioni di ogni documento.

### Cronologia Versioni

Per ogni documento puoi vedere:
- **Versione corrente** (la più recente)
- **Versioni precedenti** con data
- **Changelog** delle modifiche
- **Chi** ha fatto la modifica

### Accedere alle Versioni

1. Apri il **dettaglio documento**
2. Clicca su **"Cronologia versioni"**
3. Visualizza l'elenco delle versioni
4. Scarica una versione specifica se necessario

### Confrontare Versioni

Puoi confrontare due versioni per vedere le differenze (se il formato lo permette).

> **Nota**: Solo gli amministratori possono caricare nuove versioni.
`
    },
    {
      id: 'templates',
      title: 'Template Operativi',
      requirement: '§2.5 Template: checklist, linee guida, toolkit operativi',
      content: `
## Template Operativi

La piattaforma mette a disposizione template pronti all'uso.

### Tipi di Template

| Tipo | Descrizione | Uso |
|------|-------------|-----|
| **Checklist** | Liste di controllo | Verifiche operative |
| **Linee guida** | Procedure standard | Riferimento metodologico |
| **Toolkit** | Strumenti operativi | Attività quotidiane |
| **Modelli** | Documenti precompilati | Da personalizzare |

### Come Usare i Template

1. Trova il template nella libreria
2. **Scarica** il file
3. **Personalizza** secondo le tue esigenze
4. Utilizza per il tuo lavoro

### Suggerimenti

- I template sono **aggiornati periodicamente**
- Controlla sempre di avere la **versione più recente**
- Segnala eventuali **errori o miglioramenti** agli amministratori
`
    }
  ]
}
